/**
 * Carousel — componente compartilhado do tema ODC.
 *
 * Usado pelos blocos mediaCarousel e newsListing (variação newsCarousel).
 * Decisão D5 (docs/specs/schema-decisions.md): componente único com prop de
 * variante de layout.
 *
 * Acessibilidade/UX:
 * - setas + dots + teclado (← →) + swipe (touch)
 * - autoplay pausa em hover/foco e respeita prefers-reduced-motion
 * - aria-roledescription="carousel"/"slide" conforme WAI-ARIA APG
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'clsx';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import leftKeySVG from '@plone/volto/icons/left-key.svg';
import rightKeySVG from '@plone/volto/icons/right-key.svg';

const messages = defineMessages({
  carousel: {
    id: 'carousel.label',
    defaultMessage: 'Carousel',
  },
  previous: {
    id: 'carousel.previous',
    defaultMessage: 'Previous slide',
  },
  next: {
    id: 'carousel.next',
    defaultMessage: 'Next slide',
  },
  goToSlide: {
    id: 'carousel.goToSlide',
    defaultMessage: 'Go to slide {number}',
  },
  slideOf: {
    id: 'carousel.slideOf',
    defaultMessage: '{current} of {total}',
  },
});

const SWIPE_THRESHOLD = 40; // px

export type CarouselProps = {
  slides: React.ReactNode[];
  autoplay?: boolean;
  autoplayInterval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  variant?: string;
  ariaLabel?: string;
  className?: string;
};

/** Hook com a lógica de navegação — exportado para testes. */
export const useCarousel = (total: number) => {
  const [index, setIndex] = useState(0);

  const goTo = useCallback(
    (i: number) => {
      if (total <= 0) return;
      // Navegação circular
      setIndex(((i % total) + total) % total);
    },
    [total],
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  return { index, next, prev, goTo };
};

const Carousel = ({
  slides,
  autoplay = false,
  autoplayInterval = 5000,
  showDots = true,
  showArrows = true,
  variant,
  ariaLabel,
  className,
}: CarouselProps) => {
  const intl = useIntl();
  const total = slides.length;
  const { index, next, prev, goTo } = useCarousel(total);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (!autoplay || paused || reducedMotion || total < 2) return;
    const timer = setInterval(next, autoplayInterval);
    return () => clearInterval(timer);
  }, [autoplay, paused, reducedMotion, total, next, autoplayInterval]);

  if (total === 0) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    if (delta < 0) next();
    else prev();
  };

  return (
    <section
      className={cx('odc-carousel', className, {
        [`odc-carousel--${variant}`]: variant,
      })}
      aria-roledescription="carousel"
      aria-label={ariaLabel || intl.formatMessage(messages.carousel)}
    >
      {/* Padrão WAI-ARIA APG de carrossel: teclado/touch no contêiner; os
          mesmos controles existem como botões nativos (setas/dots). */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="odc-carousel__inner"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        onKeyDown={onKeyDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="odc-carousel__viewport">
          <div
            className={cx('odc-carousel__track', {
              'odc-carousel__track--static': reducedMotion,
            })}
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {slides.map((slide, i) => (
              <div
                className="odc-carousel__slide"
                key={i}
                role="group"
                aria-roledescription="slide"
                aria-label={intl.formatMessage(messages.slideOf, {
                  current: i + 1,
                  total,
                })}
                aria-hidden={i !== index}
              >
                {slide}
              </div>
            ))}
          </div>
        </div>

        {showArrows && total > 1 && (
          <>
            <button
              type="button"
              className="odc-carousel__arrow odc-carousel__arrow--prev"
              onClick={prev}
              aria-label={intl.formatMessage(messages.previous)}
            >
              <Icon name={leftKeySVG} size="24px" />
            </button>
            <button
              type="button"
              className="odc-carousel__arrow odc-carousel__arrow--next"
              onClick={next}
              aria-label={intl.formatMessage(messages.next)}
            >
              <Icon name={rightKeySVG} size="24px" />
            </button>
          </>
        )}

        {showDots && total > 1 && (
          <div className="odc-carousel__dots">
            {slides.map((_, i) => (
              <button
                type="button"
                key={i}
                className={cx('odc-carousel__dot', {
                  'odc-carousel__dot--active': i === index,
                })}
                onClick={() => goTo(i)}
                aria-label={intl.formatMessage(messages.goToSlide, {
                  number: i + 1,
                })}
                aria-current={i === index}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Carousel;
